/**
 * A random id that works on the browsers our users actually arrive in.
 *
 * `crypto.randomUUID()` needs Safari 15.4+ / Chrome 92+ AND a secure context.
 * It was called unguarded in the toast store, which fires during startup — so on
 * an older in-app webview the first toast took the whole app down with a
 * `TypeError`, and the user saw a black screen rather than a toast.
 *
 * The fallback is `getRandomValues` (Safari 11+, effectively universal), formatted
 * as a v4 UUID. The last resort is not cryptographically random and does not need
 * to be: every caller uses these as local correlation ids, never as secrets.
 */
export function uuid(): string {
    const cryptoApi = globalThis.crypto;

    if (typeof cryptoApi?.randomUUID === 'function') {
        try {
            return cryptoApi.randomUUID();
        } catch {
            // some webviews expose the method but throw outside a secure context
        }
    }

    if (typeof cryptoApi?.getRandomValues === 'function') {
        const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
        // v4: version in byte 6, variant in byte 8 — same shape as randomUUID,
        // so anything parsing these keeps working.
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) =>
            b.toString(16).padStart(2, '0'),
        ).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    // No crypto at all. Not random enough for anything but a local key, which is
    // all these are — and a weak id beats a crashed app.
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
